import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../config';

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isMiniApp, setIsMiniApp] = useState(false);
  const { loginWithTelegram, loginWithTelegramWebApp } = useAuth() as any;
  const navigate = useNavigate();

  // Авто-логин в Telegram Mini Apps
  useEffect(() => {
    const w = window as any;
    if (w.__tgAutoLoginStarted) return;
    const tg = (window as any).Telegram?.WebApp;
    if (tg && tg.initData) {
      w.__tgAutoLoginStarted = true;
      try { tg.ready && tg.ready(); } catch {}
      setIsMiniApp(true);
      const initData = tg.initData as string;
      if (initData && initData.length > 0) {
        setLoading(true);
        loginWithTelegramWebApp(initData)
          .then(() => {
            toast.success('Авторизация через Telegram', { id: 'auth-success' });
            navigate('/');
          })
          .catch((e: any) => {
            console.error('Telegram WebApp login error', e);
            const detail = e?.response?.data?.detail || e?.message;
            toast.error(detail ? `Не удалось авторизоваться: ${detail}` : 'Не удалось авторизоваться через Telegram', { id: 'auth-error' });
          })
          .finally(() => setLoading(false));
      }
    }
    // Виджет не используем — авторизуемся своей кнопкой через OAuth popup
  }, []);

  // Авторизация по своей кнопке — открываем OAuth Telegram в попапе и ловим ответ
  const handleTelegramLogin = async () => {
    try {
      const bot = config.getTelegramLoginBotUsername();
      const botId = config.getTelegramLoginBotId();
      if (!bot) {
        toast.error('Не задан username бота');
        return;
      }
      if (!botId) {
        toast.error('Не задан bot_id бота');
        return;
      }

      // Гарантируем загрузку скрипта виджета
      const ensureWidget = () => new Promise<void>((resolve, reject) => {
        if ((window as any).Telegram?.Login?.auth) return resolve();
        const existing = document.getElementById('telegram-login-script');
        if (existing) {
          // подождём пока инициализируется
          setTimeout(() => resolve(), 150);
          return;
        }
        const s = document.createElement('script');
        s.id = 'telegram-login-script';
        s.async = true;
        s.src = 'https://telegram.org/js/telegram-widget.js?22';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Не удалось загрузить скрипт Telegram'));
        document.body.appendChild(s);
      });

      setLoading(true);
      await ensureWidget();
      const TL = (window as any).Telegram;
      if (!TL?.Login?.auth) {
        throw new Error('Telegram.Login.auth недоступен');
      }

      TL.Login.auth({
        bot_id: Number(botId),
        bot: bot,
        request_access: 'write',
        origin: window.location.origin,
      }, async (user: any) => {
        try {
          if (!user || !user.id || !user.auth_date || !user.hash) {
            throw new Error('Некорректный ответ Telegram');
          }
          await loginWithTelegram({
            id: Number(user.id),
            auth_date: Number(user.auth_date),
            hash: String(user.hash),
            first_name: user.first_name,
            last_name: user.last_name,
            username: user.username,
            photo_url: user.photo_url,
          });
          toast.success('Авторизация через Telegram', { id: 'auth-success' });
          navigate('/');
        } catch (e) {
          console.error('Telegram auth error', e);
          toast.error('Не удалось авторизоваться через Telegram', { id: 'auth-error' });
        } finally {
          setLoading(false);
        }
      });
    } catch (e) {
      console.error(e);
      toast.error('Не удалось запустить авторизацию Telegram', { id: 'auth-error' });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#181A20] py-12 px-4 sm:px-6 lg:px-8 safe-area-top safe-area-bottom">
      <div className="max-w-md w-full">
        {/* Заголовок */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white tracking-wide">GuruApp Hub</h2>
        </div>

        {/* Если Mini App — показываем загрузочный экран в стиле карточки авторизации */}
        {isMiniApp ? (
          <div className="bg-[#23262F] rounded-xl shadow-lg border border-[#353945] p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mr-3"></div>
              <div>
                <div className="text-white font-semibold">Выполняется авторизация…</div>
                <div className="text-[#B1B5C3] text-sm">Пожалуйста, подождите</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#23262F] rounded-xl shadow-lg border border-[#353945] p-8">
            <div className="space-y-6">
              <button
                onClick={handleTelegramLogin}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#23262F]"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Авторизация...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 mr-2" fill="currentColor">
                      <path d="M9.036 15.583l-.379 5.337c.543 0 .777-.233 1.057-.512l2.538-2.431 5.255 3.847c.963.531 1.65.252 1.915-.891l3.469-16.243h.001c.307-1.436-.52-1.999-1.458-1.653L1.3 9.53c-1.407.545-1.386 1.329-.24 1.686l5.333 1.664L18.86 6.32c.62-.407 1.183-.182.719.225"/>
                    </svg>
                    Войти через Telegram
                  </div>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;